/* eslint-disable max-len */
import _get from 'lodash/get';
import _orderBy from 'lodash/orderBy';
import _sortBy from 'lodash/sortBy';
import _find from 'lodash/find';
import cloneDeep from 'lodash/cloneDeep';
import reject from 'lodash/reject';
import { findTaggedValue } from './tags';
import {
  noImgGuid,
  SortOrder,
  SortColorsBy,
  SwatchObjPropertyPaths,
} from '../common/constants';
import { Utils } from './urlUtils';

class ColorUtils {
  static COLOR_FAMILY_REGEX = /^.+;{1}(.+$)/;

  static getColorPrices = (id, colors) => {
    const choice = colors.find((cc) => cc.id === Number(id));
    if (choice != null) {
      return ({ price: choice.price, originalPrice: choice.originalPrice });
    }
    return ({ price: 0, originalPrice: 0 });
  };

  static getSampleData = (id, colors) => {
    const color = colors.find((cc) => cc.id === Number(id));
    if (color != null) {
      return ({ sampleData: color.sampleData });
    }
    return ({ sampleData: null });
  };

  static filterColorsbyFamily = (choices, colorFilterSelections, surfaces) => {
    if (!colorFilterSelections || colorFilterSelections.length === 0) {
      return choices;
    }

    const colorFamilyLabels = [];
    const secondaryColorLabels = [];
    const brandLabels = [];

    colorFilterSelections.forEach((sel) => {
      if (typeof sel === 'object') {
        if (sel.type === 'color family') {
          colorFamilyLabels.push(sel.label);
        } else if (sel.type === 'secondary-color family') {
          secondaryColorLabels.push(sel.label);
        } else if (sel.type === 'brand') {
          brandLabels.push(sel.label);
        }
      } else {
        colorFamilyLabels.push(sel);
      }
    });

    if (brandLabels.length > 0 && !surfaces) {
      return [];
    }

    const filterByTagType = (items, filterType, labels) => {
      return items.filter((choice) => {
        return choice.tags.some((tag) => {
          if (!tag.includes(`filter:${filterType};`)) return false;
          const tagLabel = tag.substring(tag.indexOf(';') + 1);
          return labels.includes(tagLabel);
        });
      });
    };

    let result = choices;

    if (colorFamilyLabels.length > 0) {
      result = filterByTagType(result, 'color family', colorFamilyLabels);
    }

    if (secondaryColorLabels.length > 0) {
      result = filterByTagType(result, 'secondary-color family', secondaryColorLabels);
    }

    if (brandLabels.length > 0) {
      result = result.filter((choice) => {
        const surface = surfaces.find((surf) => Number(surf.surfaceCode) === Number(choice.surfaceCode));
        return surface && brandLabels.includes(surface.desc);
      });
    }

    return result;
  };

  static imageUrlHasNoImageGuid = (imageUrl) => imageUrl && imageUrl.includes(noImgGuid);

  static doesNotHaveImage =
    (color, propertyPathForImageUrl) => this.imageUrlHasNoImageGuid(_get(color, propertyPathForImageUrl));

  static hasImage =
    (color, propertyPathForImageUrl) => !this.imageUrlHasNoImageGuid(_get(color, propertyPathForImageUrl));

  static filterColorsWithImage =
    (colors, propertyPathForImageUrl) => colors.filter((color) => this.hasImage(color, propertyPathForImageUrl));

  static filterColorsWithoutImage =
    (colors, propertyPathForImageUrl) => colors.filter(
      (color) => this.doesNotHaveImage(color, propertyPathForImageUrl),
    );

  static sortColorsByPriceAscAndPropertyPathForColorNameAsc =
    (colors, propertyPathForColorName) => {
      const sortablePropName = 'sortableNumPrice';
      return _sortBy(
        this.getColorsArrayWithSortableNumPrice(colors, sortablePropName),
        [sortablePropName, SwatchObjPropertyPaths[propertyPathForColorName]]);
    };

  static sortColorsByPriceDescButPropertyPathForColorNameAsc =
    (colors, propertyPathForColorName) => {
      const sortablePropName = 'sortableNumPrice';
      return _orderBy(
        this.getColorsArrayWithSortableNumPrice(colors, sortablePropName),
        [sortablePropName, SwatchObjPropertyPaths[propertyPathForColorName]],
        [SortOrder.Desc]);
    };

  static getColorsArrayWithSortableNumPrice =
    (colors, sortablePropName) => {
      return colors.map((obj) => ({ ...obj, [sortablePropName]: Number(obj[SwatchObjPropertyPaths.price]) }));
    };

  static sortColorsWithImagesByPriceAscAndPropertyPathForColorNameAsc =
    (colors, propertyPathForColorName, propertyPathForImageUrl) => {
      const colorsWithImage = this.filterColorsWithImage(colors, propertyPathForImageUrl);
      return this.sortColorsByPriceAscAndPropertyPathForColorNameAsc(colorsWithImage, propertyPathForColorName);
    };

  static featureSort = (colors, propertyPathForColorName, propertyPathForImageUrl) => {
    const colorsWithoutImage = this.filterColorsWithoutImage(colors, propertyPathForImageUrl);
    const colorsWithImage = this.filterColorsWithImage(colors, propertyPathForImageUrl);
    const sortedColorsWithImage = this.sortColorsByPriceAscAndPropertyPathForColorNameAsc(
      colorsWithImage,
      propertyPathForColorName,
    );
    const sortedColorsWithoutImage = this.sortColorsByPriceAscAndPropertyPathForColorNameAsc(
      colorsWithoutImage,
      propertyPathForColorName,
    );
    const fullArr = sortedColorsWithImage.concat(sortedColorsWithoutImage);

    return fullArr;
  };

  static sortColors = (colors, sortBy, propertyPathForColorName, propertyPathForImageUrl) => {
    switch (sortBy) {
    case SortColorsBy.Featured: return this.featureSort(colors, propertyPathForColorName, propertyPathForImageUrl);
    case SortColorsBy.PriceAsc: return this.sortColorsByPriceAscAndPropertyPathForColorNameAsc(
      colors,
      propertyPathForColorName,
    );
    case SortColorsBy.PriceDesc: return this.sortColorsByPriceDescButPropertyPathForColorNameAsc(
      colors,
      propertyPathForColorName,
    );
    case SortColorsBy.NameAsc: return _sortBy(colors, [SwatchObjPropertyPaths[propertyPathForColorName]]);
    case SortColorsBy.NameDesc: return _orderBy(
      colors,
      [SwatchObjPropertyPaths[propertyPathForColorName]],
      [SortOrder.Desc],
    );
    default: return colors;
    }
  };

  static getLowestColorPrice = (colorSwatches) => {
    let price = 0;
    if (colorSwatches.some((xx) => xx.price > 0)) {
      const sortedColors = this.sortColors(colorSwatches, SortColorsBy.PriceAsc);
      const lowestColor = sortedColors && sortedColors[0];
      if (lowestColor) {
        price = lowestColor.price;
      }
    }

    return price;
  };

  static getCheapestColorSwatchWithImage = (colors) => {
    const sortedColorsWithImages = this.sortColorsWithImagesByPriceAscAndPropertyPathForColorNameAsc(
      colors,
      SwatchObjPropertyPaths.name,
      SwatchObjPropertyPaths.imageUrl,
    );
    return sortedColorsWithImages[0];
  };

  static getSortedAndFilteredColors = (
    colors,
    selectedColorFilters,
    selectedColorSort,
    preselectedSampleColor,
    ignorePreselectedColor,
    isCollapsed,
    surfaces,
  ) => {
    let colorsCopy = cloneDeep(colors);
    const sortedAndFilteredColors = [];
    if (colorsCopy && colorsCopy.length > 0) {
      if (!ignorePreselectedColor && preselectedSampleColor) {
        if (Utils.hasPlaGuid() && preselectedSampleColor) {
          const preselectedColorIndex = colorsCopy.findIndex((color) => color.id === preselectedSampleColor.id);
          if (preselectedColorIndex !== -1) {
            const preselectedColor = colorsCopy.splice(preselectedColorIndex, 1)[0];
            sortedAndFilteredColors.push(preselectedColor);
          } else {
            sortedAndFilteredColors.push(preselectedSampleColor);
            colorsCopy = reject(colorsCopy, (color) => color.sampleOmsId === preselectedSampleColor.sampleOmsId);
          }
        }
      }
    }
    const filteredColors = this.filterColorsbyFamily(colorsCopy, selectedColorFilters, surfaces);
    const sortedColors = this.sortColors(
      filteredColors,
      selectedColorSort,
      SwatchObjPropertyPaths.name,
      SwatchObjPropertyPaths.imageUrl,
    );

    if (isCollapsed) {
      const screenWidth = window?.innerWidth;
      let slices = 10;

      if (screenWidth <= 1024) {
        slices = 6;
      }
      const firstColors = sortedAndFilteredColors.concat(sortedColors).slice(0, slices);
      const isDupe = firstColors.find((color) => color?.sampleOmsId === preselectedSampleColor?.sampleOmsId || color?.id === preselectedSampleColor?.id);

      return !isDupe ? sortedAndFilteredColors.concat(firstColors) : firstColors;
    }

    return sortedAndFilteredColors.concat(sortedColors);
  };

  static getSearchSuggestions = (searchTerm, colors, maxSuggestions = 8) => {
    if (!searchTerm || searchTerm.length < 2) {
      return [];
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    const suggestions = new Set();

    colors.forEach((color) => {
      const colorName = (color?.name || color?.shortLabel)?.toLowerCase();
      const colorFamily = findTaggedValue(color?.tags, 'filter')?.match(ColorUtils.COLOR_FAMILY_REGEX)?.[1]?.toLowerCase();

      if (colorName?.includes(lowerSearchTerm)) {
        suggestions.add(color.name || color.shortLabel);
      }

      if (colorFamily?.includes(lowerSearchTerm)) {
        suggestions.add(colorFamily.charAt(0).toUpperCase() + colorFamily.slice(1));
      }
    });

    return Array.from(suggestions).slice(0, maxSuggestions);
  };

  static handleSuggestionKeyDown = (
    event,
    searchSuggestions,
    activeSuggestionIndex,
    setActiveSuggestionIndex,
    setSearchSuggestions,
    handleSuggestionClick
  ) => {
    if (searchSuggestions.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveSuggestionIndex((prev) => (
        prev < searchSuggestions.length - 1 ? prev + 1 : prev
      ));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveSuggestionIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (event.key === 'Enter' && activeSuggestionIndex >= 0) {
      event.preventDefault();
      handleSuggestionClick(searchSuggestions[activeSuggestionIndex]);
    } else if (event.key === 'Escape') {
      setSearchSuggestions([]);
      setActiveSuggestionIndex(-1);
    }
  };

  static filterColorsBySearchTerm = (colors, searchTerm, nameProperty = 'name') => {
    if (!searchTerm || searchTerm === '') {
      return colors;
    }

    const lowerSearchTerm = searchTerm.toLowerCase();

    return colors.filter((color) => {
      const nameTest = color?.[nameProperty]?.toLowerCase()?.includes(lowerSearchTerm);
      const colorFamily = findTaggedValue(color?.tags, 'filter')?.match(ColorUtils.COLOR_FAMILY_REGEX)?.[1];
      const colorTest = colorFamily?.toLowerCase()?.includes(lowerSearchTerm);
      return nameTest || colorTest;
    });
  };
}

export default ColorUtils;
